<template>
    <!-- dashboard analytics start -->
    <v-container>
      <!-- payment bar chart start -->
      <v-row v-if="roleName === 'Admin'">
        <v-col cols="12">
          <v-card class="pa-4 chart-card elevation-3">
            <v-card-title class="text-h6 font-weight-bold primary--text">
              {{ $t('dashboardChart1') }}
            </v-card-title>
            <v-card-text>
              <bar-chart v-if="loadPayment" :chartdata="paymentData" :options="barChartOptions" height="300" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
  
      <!-- date-wise participation line chart start -->
      <v-row>
        <v-col cols="12">
          <v-card class="pa-4 chart-card elevation-3">
            <v-card-title class="text-h6 font-weight-bold primary--text">
              {{ $t('dashboardChart2') }}
            </v-card-title>
            <v-card-text>
              <line-chart v-if="loadQuiz" :chartdata="quizCountdata" :options="chartOptions" height="300" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
  
      <!-- topic-wise participation bar chart start -->
      <v-row>
        <v-col cols="12">
          <v-card class="pa-4 chart-card elevation-3">
            <v-card-title class="text-h6 font-weight-bold primary--text">
              {{ $t('dashboardChart3') }}
            </v-card-title>
            <v-card-text>
              <bar-chart v-if="loadUser" :chartdata="quizUserCountdata" :options="barChartOptions" height="300" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
  
      <!-- different pie charts start -->
      <v-row>
        <v-col cols="12" md="4">
          <v-card class="pa-4 chart-card elevation-3 mb-4">
            <v-card-title class="text-h6 font-weight-bold primary--text">
              Month-wise Login
            </v-card-title>
            <v-card-text>
              <pie-chart v-if="loadMonth" :chartdata="monthdata" :options="chartOptions" height="250" />
            </v-card-text>
          </v-card>
        </v-col>
  
        <v-col cols="12" md="4">
          <v-card class="pa-4 chart-card elevation-3 mb-4">
            <v-card-title class="text-h6 font-weight-bold primary--text">
              Browser-wise Login
            </v-card-title>
            <v-card-text>
              <pie-chart v-if="loadBrowser" :chartdata="browserdata" :options="chartOptions" height="250" />
            </v-card-text>
          </v-card>
        </v-col>
  
        <v-col cols="12" md="4">
          <v-card class="pa-4 chart-card elevation-3 mb-4">
            <v-card-title class="text-h6 font-weight-bold primary--text">
              Platform-wise Login
            </v-card-title>
            <v-card-text>
              <pie-chart v-if="loadPlatform" :chartdata="platformdata" :options="chartOptions" height="250" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
  
      <!-- date-wise login line chart start -->
      <v-row>
        <v-col cols="12">
          <v-card class="pa-4 chart-card elevation-3">
            <v-card-title class="text-h6 font-weight-bold primary--text">
              {{ $t('dashboardChart4') }}
            </v-card-title>
            <v-card-text>
              <line-chart v-if="loadDate" :chartdata="datedata" :options="chartOptions" height="300" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <!-- dashboard analytics end -->
  </template>
  
  <script>
  import LineChart from '../../assets/chart/lineChart';
  import BarChart from '../../assets/chart/barChart';
  import PieChart from '../../assets/chart/pieChart';
  
  export default {
    name: 'Analytics',
    components: {
      LineChart,
      BarChart,
      PieChart,
    },
    data() {
      return {
        loadPayment: false,
        paymentData: null,
        loadQuiz: false,
        quizCountdata: null,
        chartOptions: null,
        loadUser: false,
        quizUserCountdata: null,
        barChartOptions: null,
        loadMonth: false,
        monthdata: null,
        loadBrowser: false,
        browserdata: null,
        loadPlatform: false,
        platformdata: null,
        loadDate: false,
        datedata: null,
        userId: null,
        userInfo: null,
      };
    },
    computed: {
      roleName() {
        return this.userInfo?.roleName || '';
      },
    },
    created() {
      this.userInfo = this.$store.getters['dashboard/userInfo'];
    },
    mounted() {
      this.userId = parseInt(localStorage.getItem('loggedUserId'), 10);
  
      // Initialize all load flags to false
      this.resetLoadFlags();
  
      // Fetch all data
      this.fetchPaymentData();
      this.fetchQuizData();
      this.fetchQuizUserData();
      this.fetchMonthData();
      this.fetchBrowserData();
      this.fetchPlatformData();
      this.fetchDateData();
  
      // Set chart options
      this.setChartOptions();
    },
    methods: {
      resetLoadFlags() {
        this.loadPayment = false;
        this.loadQuiz = false;
        this.loadUser = false;
        this.loadMonth = false;
        this.loadBrowser = false;
        this.loadPlatform = false;
        this.loadDate = false;
      },
      async fetchPaymentData() {
        if (this.roleName === 'SuperAdmin' || this.roleName === 'Admin') {
          try {
            const response = await this.$store.dispatch('dashboard/fetchPaymentCount', this.userId);
            if (response.status === 200 && Array.isArray(response.data)) {
              this.paymentData = {
                labels: response.data.map((x) => x.date.substr(0, 10)),
                datasets: [
                  {
                    label: this.$t('dashboardChart1'),
                    backgroundColor: '#42424',
                    data: response.data.map((x) => x.count),
                  },
                ],
              };
              this.loadPayment = true;
            }
          } catch (err) {
            console.error('Error fetching payment data:', err);
            this.$router.push({ name: 'NoInternet' });
          }
        }
      },
      async fetchQuizData() {
        try {
          const response = await this.$store.dispatch('dashboard/fetchQuizCount', this.userId);
          if (response.status === 200 && Array.isArray(response.data)) {
            const sortedData = response.data.sort((a, b) => new Date(a.date.substr(0, 10)) - new Date(b.date.substr(0, 10)));
            this.quizCountdata = {
              labels: sortedData.map((x) => x.date.substr(0, 10)),
              datasets: [
                {
                  label: this.$t('dashboardChart2'),
                  backgroundColor: 'rgba(33, 150, 243, 0.2)',
                  borderColor: '#2196F3',
                  pointBackgroundColor: '#2196F3',
                  pointBorderColor: '#fff',
                  pointHoverBackgroundColor: '#fff',
                  pointHoverBorderColor: '#2196F3',
                  borderWidth: 3,
                  pointRadius: 4,
                  tension: 0.4,
                  fill: true,
                  data: sortedData.map((x) => x.count),
                },
              ],
            };
            this.loadQuiz = true;
          }
        } catch (err) {
          console.error('Error fetching quiz data:', err);
          this.$router.push({ name: 'NoInternet' });
        }
      },
      async fetchQuizUserData() {
        try {
          const response = await this.$store.dispatch('dashboard/fetchQuizUserCount', this.userId);
          if (response.status === 200 && Array.isArray(response.data)) {
            const backgroundColors = this.getBackgroundColors(response.data);
            this.quizUserCountdata = {
              labels: response.data.map((x) => x.quizTitle),
              datasets: [
                {
                  label: this.$t('dashboardChart3'),
                  backgroundColor: backgroundColors,
                  borderColor: backgroundColors,
                  borderWidth: 1,
                  borderRadius: 5,
                  hoverBackgroundColor: backgroundColors,
                  data: response.data.map((x) => x.count),
                },
              ],
            };
            this.loadUser = true;
          }
        } catch (err) {
          console.error('Error fetching quiz user data:', err);
          this.$router.push({ name: 'NoInternet' });
        }
      },
      async fetchMonthData() {
        try {
          const response = await this.$store.dispatch('dashboard/fetchMonthCount', this.userId);
          if (response.status === 200 && Array.isArray(response.data)) {
            const backgroundColors = this.getBackgroundColors(response.data);
            this.monthdata = {
              labels: response.data.map((x) => x.month),
              datasets: [
                {
                  label: 'Login(Month wise)',
                  backgroundColor: backgroundColors,
                  borderColor: '#fff',
                  borderWidth: 2,
                  hoverBackgroundColor: backgroundColors,
                  data: response.data.map((x) => x.count),
                },
              ],
            };
            this.loadMonth = true;
          }
        } catch (err) {
          console.error('Error fetching month data:', err);
          this.$router.push({ name: 'NoInternet' });
        }
      },
      async fetchBrowserData() {
        try {
          const response = await this.$store.dispatch('dashboard/fetchBrowserCount', this.userId);
          if (response.status === 200 && Array.isArray(response.data)) {
            const backgroundColors = this.getBackgroundColors(response.data);
            this.browserdata = {
              labels: response.data.map((x) => x.browser),
              datasets: [
                {
                  label: 'Login(Browser wise)',
                  backgroundColor: backgroundColors,
                  borderColor: '#fff',
                  borderWidth: 2,
                  hoverBackgroundColor: backgroundColors,
                  data: response.data.map((x) => x.count),
                },
              ],
            };
            this.loadBrowser = true;
          }
        } catch (err) {
          console.error('Error fetching browser data:', err);
          this.$router.push({ name: 'NoInternet' });
        }
      },
      async fetchPlatformData() {
        try {
          const response = await this.$store.dispatch('dashboard/fetchPlatformCount', this.userId);
          if (response.status === 200 && Array.isArray(response.data)) {
            const backgroundColors = this.getBackgroundColors(response.data);
            this.platformdata = {
              labels: response.data.map((x) => x.platform),
              datasets: [
                {
                  label: 'Login(Platform wise)',
                  backgroundColor: backgroundColors,
                  borderColor: '#fff',
                  borderWidth: 2,
                  hoverBackgroundColor: backgroundColors,
                  data: response.data.map((x) => x.count),
                },
              ],
            };
            this.loadPlatform = true;
          }
        } catch (err) {
          console.error('Error fetching platform data:', err);
          this.$router.push({ name: 'NoInternet' });
        }
      },
      async fetchDateData() {
        try {
          const response = await this.$store.dispatch('dashboard/fetchDateCount', this.userId);
          if (response.status === 200 && Array.isArray(response.data)) {
            // Get a blue color from the getBackgroundColors method
            const blueColors = this.getBackgroundColors(response.data);
            const mainBlue = blueColors[0]; // Use the first blue color
            
            this.datedata = {
              labels: response.data.map((x) => x.date.substr(0, 10)),
              datasets: [
                {
                  label: this.$t('dashboardChart4'),
                  backgroundColor: `${mainBlue}33`, // Adding transparency (33 = 20% opacity in hex)
                  borderColor: mainBlue,
                  pointBackgroundColor: mainBlue,
                  pointBorderColor: '#fff',
                  pointHoverBackgroundColor: '#fff',
                  pointHoverBorderColor: mainBlue,
                  borderWidth: 3,
                  pointRadius: 4,
                  tension: 0.4,
                  fill: true,
                  data: response.data.map((x) => x.count),
                },
              ],
            };
            this.loadDate = true;
          }
        } catch (err) {
          console.error('Error fetching date data:', err);
        }
      },
      setChartOptions() {
        this.chartOptions = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              labels: {
                font: {
                  size: 14,
                },
                padding: 20,
              },
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 12,
              titleFont: {
                size: 14,
              },
              bodyFont: {
                size: 13,
              },
              displayColors: true,
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1,
            },
          },
          animation: {
            duration: 1500,
          },
        };
  
        this.barChartOptions = {
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.05)',
              },
            },
            x: {
              grid: {
                display: false,
              },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              labels: {
                font: {
                  size: 14,
                },
                padding: 20,
              },
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 12,
              titleFont: {
                size: 14,
              },
              bodyFont: {
                size: 13,
              },
              displayColors: true,
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1,
            },
          },
          animation: {
            duration: 1500,
          },
        };
      },
      getBackgroundColors(data) {
        // Blue palette colors
        const blueColors = [
          '#0D47A1', '#1565C0', '#1976D2', '#1E88E5', '#2196F3',
          '#42A5F5', '#64B5F6', '#90CAF9', '#BBDEFB', '#E3F2FD',
          '#0277BD', '#0288D1', '#039BE5', '#03A9F4', '#29B6F6'
        ];
        
        // Return colors from the blue palette based on data length
        return data.map((_, index) => blueColors[index % blueColors.length]);
      },
    },
  };
  </script>
  
  <style scoped>
  .chart-card {
    border-radius: 12px;
    transition: all 0.3s ease;
    overflow: hidden;
  }
  
  .chart-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 20px rgba(0, 0, 0, 0.1) !important;
  }
  
  .v-card-title {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    padding-bottom: 16px;
  }
  
  .v-card-text {
    padding-top: 16px;
  }
  </style>